import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getDashboard, formatApiDate, locationFilterParams } from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import {
  buildFallbackSections,
  buildModuleNavigateUrl,
  cardBackground,
  formatDashboardCardValue,
  mergeNavigateQuery,
} from "./utils/dashboardNavigation";

const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };

function DashboardCard({ card, onClick, selected }) {
  const clickable = card.clickable !== false && card.navigate;
  const bg = cardBackground(card.color);

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={card.id || card.label}>
      <Card
        elevation={0}
        onClick={clickable ? () => onClick(card) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick(card);
                }
              }
            : undefined
        }
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        sx={{
          bgcolor: bg,
          color: "#fff",
          borderRadius: 2,
          cursor: clickable ? "pointer" : "default",
          transition: "transform 0.15s, box-shadow 0.2s",
          outline: selected ? "3px solid rgba(255,255,255,0.85)" : "none",
          "&:hover": clickable ? { transform: "translateY(-2px)", boxShadow: 4 } : {},
        }}
      >
        <CardContent sx={{ py: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
            {card.label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {formatDashboardCardValue(card)}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default function VMSuperAdminDashboard() {
  const navigate = useNavigate();
  const { adminScope, locationLocked } = useAdminPanel();
  const loc = useLocationCascading();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const buildFilters = useCallback(() => {
    const params = {
      ...locationFilterParams(loc, adminScope, { locked: locationLocked }),
    };
    const from = formatApiDate(fromDate);
    const to = formatApiDate(toDate);
    if (from) params.from = from;
    if (to) params.to = to;
    return params;
  }, [fromDate, toDate, loc, adminScope, locationLocked]);

  const fetchDashboardStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await getDashboard(buildFilters());
      setDashboardData(res.data?.data || null);
    } catch {
      setDashboardData(null);
      toast.info("Dashboard API unavailable.");
    } finally {
      setLoadingStats(false);
    }
  }, [buildFilters]);

  useEffect(() => {
    fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => {
    if (!dashboardData) return [];
    if (Array.isArray(dashboardData.sections) && dashboardData.sections.length > 0) {
      return dashboardData.sections;
    }
    return buildFallbackSections(dashboardData);
  }, [dashboardData]);

  const filtersApplied = dashboardData?.filters_applied || {};

  const handleCardClick = (card) => {
    if (!card.clickable || !card.navigate) return;
    const navigateMeta = mergeNavigateQuery(card.navigate, filtersApplied);
    const url = buildModuleNavigateUrl(navigateMeta);
    if (!url) {
      toast.info("Navigation not configured for this card.");
      return;
    }
    navigate(url);
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    if (!locationLocked) {
      loc.resetLocation();
    }
    window.setTimeout(() => fetchDashboardStats(), 0);
  };

  return (
    <Box>
      <SuperAdminPageHeader
        showBack={false}
        title="Welcome to Venue Manager Admin Control Panel"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={loadingStats}>
              Reset
            </Button>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters
            </Button>
          </Stack>
        }
      />

      <Collapse in={filtersOpen} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Date range
            </Typography>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={setFromDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={setToDate}
                  format="DD/MM/YYYY"
                  minDate={fromDate?.isValid?.() ? fromDate : undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Location
            </Typography>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={FILTER_ITEM}
              />
              <Grid item {...FILTER_ITEM} sx={{ display: "flex", alignItems: "flex-end" }}>
                <Button variant="contained" onClick={fetchDashboardStats} disabled={loadingStats} fullWidth>
                  {loadingStats ? <CircularProgress size={22} color="inherit" /> : "Search"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Collapse>

      {loadingStats && !dashboardData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        sections.map((section) => (
          <Box key={section.id || section.title} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
              {section.title || section.id}
            </Typography>
            <Grid container spacing={2}>
              {(section.cards || []).map((card) => (
                <DashboardCard key={card.id || card.label} card={card} onClick={handleCardClick} />
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
}
